import { useState } from "react";
import { ApiException } from "../../../../../services/api/ApiException";
import { toast } from "react-toastify";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { useConfirm } from "../../../../../hooks/useConfirm";
import vectorIcon from "../../../../../assets/_VectorIcon_.svg";

import styles from "./styles.module.scss";
import { IPosition } from "../../../../../@types/position.type";
import { addOrUpdatePosition, deletePosition } from "../../../../../services/api/positions/PositionsService";

const schema = z.object({
  id: z.string().optional().nullable(),
  positionName: z
    .string()
    .min(1, "Nome da Função é obrigatória")
    .max(50, "Nome da Função deve ter no máximo 50 caracteres")
});

interface FormPositionsProps {
  pageType: "create" | "update" | "seeMore";
  positionsData?: IPosition;
  isSyncActive?: boolean;
}

export const FormPositions: React.FC<FormPositionsProps> = ({
  pageType,
  positionsData,
  isSyncActive = false,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IPosition>({
    resolver: zodResolver(schema),
    defaultValues: {
      id: positionsData?.id || undefined,
      positionName: positionsData?.positionName || ""
    },
  });

  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const handleOpenModalConfirm = () => setOpenModalConfirm(true);
  const handleCloseModalConfirm = () => setOpenModalConfirm(false);

  const { showConfirm, Confirm } = useConfirm(
    "Tem certeza que deseja excluir a Função?",
    openModalConfirm,
    handleCloseModalConfirm
  );

  const navigate = useNavigate();

  const onSubmit = async (data: IPosition) => {
    try {
      setLoading(true);
      await addOrUpdatePosition(data);
      reset();
      toast.success(
        "Função salva com sucesso!"
      );
      navigate("/configurations/positions");
    } catch (error: any) {
      toast.error(
        positionsData
          ? "Erro ao alterar a Função!"
          : "Erro ao criar Função"
      );
      new ApiException(error.message || "Erro ao processar a requisição.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async () => {
    handleOpenModalConfirm();
    const isConfirm = await showConfirm();

    if (isConfirm) {
      try {
        await deletePosition(positionsData?.id as string);
        toast.success("Função excluída com sucesso!");
        navigate("/configurations/positions");
      } catch (error) {
        console.log(error);
        toast.error(
          "Erro ao excluir a Função! Tente novamente mais tarde."
        );
      }
    }
  };

  return (
    <>
      <div className={styles.back}>
        <Button
          className={styles.button}
          onClick={() => navigate("/configurations/positions")}
        >
          {vectorIcon && <img src={vectorIcon} />}
          Voltar a tela anterior
        </Button>
      </div>
      <div className={styles.title}>
        <h1>
          {pageType === "update"
            ? `${positionsData?.positionName}`
            : "Nova Função"}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className={styles.formPosition}>
        <div className={styles.fields}>
          <Controller
            name="positionName"
            control={control}
            render={({ field }) => (
              <TextField
                label="Nome da Função"
                placeholder={
                  pageType === "create"
                    ? "Insira o nome da Função aqui..."
                    : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                value={field.value}
                onChange={field.onChange}
                error={!!errors.positionName}
                helperText={errors.positionName?.message}
                fullWidth
                disabled={isSyncActive}
              />
            )}
          />
        </div>

        <div className={styles.btnContainer}>
          {positionsData?.id && (
            <Button
              variant="contained"
              color="secondary"
              type="button"
              onClick={handleDeleteClick}
              className={styles.btnsDeleted}
              disabled={loading || isSyncActive}
            >
              Excluir Função
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            className={styles.btnsSave}
            disabled={loading || isSyncActive}
          >
            Salvar
          </Button>
        </div>
      </form>
      {Confirm}
    </>
  );
};
